/**
 * The project protocol's state and calls in the browser. Live: state.ext['project-protocol'],
 * kept current by SSE 'ext' events, and the /api/project-protocol routes (part B of R-PP).
 * Demo (?mock=1): the same calls applied to demo data, with runs and dispatches that move on
 * by themselves so the dialogs show their live states.
 */
import { useEffect, useState } from 'react';
import { PROJECT_PROTOCOL_EXT } from '@orgus/protocol';
import type { FixDispatch, Id, NativeCheckSettings, ProjectActionRun, ProjectProtocolState, YieldHold } from '@orgus/protocol';
import { toUiError, isModuleAbsent } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { api, apiGet } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { createMockProjectProtocol } from './mock.ts';
import { busyOn, declaredAction, ppSlice, reportFor } from './model.ts';

const MOCK_ACTOR = 'owner';
const isMock = () => useOrgus.getState().mode === 'mock';
const current = (): ProjectProtocolState | undefined => ppSlice(useOrgus.getState().ext as Record<string, unknown> | undefined);

/** Replace the slice the way an SSE 'ext' state event does, so the scoped view keeps it too. */
export function setPpState(s: ProjectProtocolState): void {
  useOrgus.getState().applyExt({ ext: PROJECT_PROTOCOL_EXT, type: 'state', data: s });
}

function update(fn: (s: ProjectProtocolState) => ProjectProtocolState): void {
  const s = current();
  if (s) setPpState(fn(s));
}

const upsert = <T extends { id: Id }>(list: T[], x: T): T[] => (list.some((y) => y.id === x.id) ? list.map((y) => (y.id === x.id ? x : y)) : [x, ...list]);

/** Merge what a call answered, so the view moves on before the SSE update arrives. */
const putRun = (r: ProjectActionRun) => update((s) => ({ ...s, runs: upsert(s.runs, r) }));
const putDispatch = (d: FixDispatch) => update((s) => ({ ...s, dispatches: upsert(s.dispatches, d) }));
const putYield = (h: YieldHold) => update((s) => ({ ...s, yields: upsert(s.yields, h) }));

export function usePpSlice(): ProjectProtocolState | undefined {
  return useOrgus((s) => ppSlice(s.ext as Record<string, unknown> | undefined));
}

export interface PpLoad {
  state: ProjectProtocolState | undefined;
  phase: 'loading' | 'ready' | 'error';
  error: UiError | null;
  retry: () => Promise<void>;
}

/** The protocol state with its loading and error states. Demo mode fills it with demo data. */
export function useProjectProtocol(): PpLoad {
  const state = usePpSlice();
  const mode = useOrgus((s) => s.mode);
  const storePhase = useOrgus((s) => s.phase);
  const storeError = useOrgus((s) => s.loadError);
  const [error, setError] = useState<UiError | null>(null);

  const fetchOnce = async () => {
    setError(null);
    try {
      setPpState(await apiGet<ProjectProtocolState>('/api/project-protocol'));
    } catch (e) {
      const ui = toUiError(e);
      setError(
        ui.status === 404 && !isModuleAbsent(ui)
          ? { what: 'This hub does not read the project protocol yet.', why: 'It answered 404 for /api/project-protocol, so it is an older version.', next: 'Update Orgus on the hub machine and restart it.', status: 404 }
          : ui,
      );
    }
  };

  useEffect(() => {
    if (state) return;
    if (mode === 'mock') {
      if (storePhase === 'ready') setPpState(createMockProjectProtocol());
      return;
    }
    if (storePhase === 'ready') void fetchOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, mode, storePhase]);

  if (state) return { state, phase: 'ready', error: null, retry: fetchOnce };
  if (storePhase === 'error') return { state, phase: 'error', error: storeError, retry: fetchOnce };
  if (error) return { state, phase: 'error', error, retry: fetchOnce };
  return { state, phase: 'loading', error: null, retry: fetchOnce };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

let seq = 0;
const newId = (prefix: string) => `${prefix}_${Date.now().toString(36)}${(seq++).toString(36)}`;

/** Demo: a run that started finishes a moment later (a held one waits for its hold). */
function mockFinish(runId: Id, ms = 2400): void {
  setTimeout(() => {
    update((s) => {
      const r = s.runs.find((x) => x.id === runId);
      if (!r || r.state !== 'running') return s;
      const held = r.yieldId && s.yields.some((h) => h.id === r.yieldId && h.state === 'waiting');
      if (held) return s;
      return { ...s, runs: upsert(s.runs, { ...r, state: 'succeeded', endedAt: Date.now(), exitCode: 0, logRef: `run:${r.id}` }) };
    });
  }, ms);
}

/**
 * Ask the hub to run a declared action, and confirm it at once: the dialog that called this is
 * the confirmation. Refuses when the hub's command (read at HEAD) is not the one the person saw.
 */
export async function runDeclaredAction(input: { projectId: Id; machineId: Id; actionId: string; shownCommand: string }): Promise<ProjectActionRun> {
  const { projectId, machineId, actionId, shownCommand } = input;
  if (isMock()) {
    const s = current();
    const decl = declaredAction(reportFor(s, projectId, machineId), actionId);
    if (!s || !decl) throw { what: 'The action is no longer declared.', why: "It is not in the project's orgus.yml at HEAD.", next: 'Close this dialog and check the project.' };
    const now = Date.now();
    const busy = decl.heavy ? busyOn(s, machineId).find((b) => b.projectId !== projectId) : undefined;
    const runId = newId('run');
    const hold: YieldHold | null = busy
      ? { id: newId('yld'), kind: 'declared-action', label: decl.label, workId: runId, projectId, machineId, for: busy, since: now, until: now + 30 * 60_000, state: 'waiting' }
      : null;
    const run: ProjectActionRun = {
      id: runId,
      projectId,
      machineId,
      actionId,
      command: decl.run,
      cwd: decl.cwd,
      state: 'running',
      requestedBy: MOCK_ACTOR,
      confirmedBy: MOCK_ACTOR,
      ...(hold ? { yieldId: hold.id } : { startedAt: now }),
    };
    update((x) => ({ ...x, runs: [run, ...x.runs], yields: hold ? [hold, ...x.yields] : x.yields }));
    mockFinish(runId);
    return run;
  }
  let run = await api<ProjectActionRun>('/api/project-protocol/actions/run', { method: 'POST', body: { projectId, machineId, actionId } });
  if (run.command !== shownCommand) {
    if (run.state === 'awaiting-confirmation') await api<ProjectActionRun>(`/api/project-protocol/actions/${encodeURIComponent(run.id)}/cancel`, { method: 'POST', body: {} }).catch(() => undefined);
    throw {
      what: 'The command changed.',
      why: `orgus.yml at HEAD now runs "${run.command}", not the command shown here.`,
      next: 'Close this dialog, open the action again and check the new command.',
    };
  }
  if (run.state === 'awaiting-confirmation') {
    run = await api<ProjectActionRun>(`/api/project-protocol/actions/${encodeURIComponent(run.id)}/confirm`, { method: 'POST', body: {} });
  }
  putRun(run);
  return run;
}

/** Confirm a run that waits for a person (one the board's buttons requested on the hub). */
export async function confirmRun(runId: Id): Promise<ProjectActionRun | undefined> {
  if (isMock()) {
    const r = current()?.runs.find((x) => x.id === runId);
    if (!r) return undefined;
    const next: ProjectActionRun = { ...r, state: 'running', confirmedBy: MOCK_ACTOR, startedAt: Date.now() };
    putRun(next);
    mockFinish(runId);
    return next;
  }
  const run = await api<ProjectActionRun>(`/api/project-protocol/actions/${encodeURIComponent(runId)}/confirm`, { method: 'POST', body: {} });
  putRun(run);
  return run;
}

/** Read the whole state again (after the hub answered with a run the stream has not brought yet). */
export async function refreshProjectProtocol(): Promise<ProjectProtocolState | undefined> {
  if (isMock()) return current();
  const s = await apiGet<ProjectProtocolState>('/api/project-protocol');
  setPpState(s);
  return s;
}

export async function cancelRun(runId: Id): Promise<ProjectActionRun | undefined> {
  if (isMock()) {
    const s = current();
    const r = s?.runs.find((x) => x.id === runId);
    if (!r) return undefined;
    const next: ProjectActionRun = { ...r, state: 'cancelled', endedAt: Date.now() };
    update((x) => ({
      ...x,
      runs: upsert(x.runs, next),
      yields: x.yields.map((h) => (h.id === r.yieldId && h.state === 'waiting' ? { ...h, state: 'released', releasedAt: Date.now() } : h)),
    }));
    return next;
  }
  const run = await api<ProjectActionRun>(`/api/project-protocol/actions/${encodeURIComponent(runId)}/cancel`, { method: 'POST', body: {} });
  putRun(run);
  return run;
}

/** "Start now": the held work starts at once, without waiting for the landing. */
export async function startHoldNow(yieldId: Id): Promise<YieldHold | undefined> {
  if (isMock()) {
    const s = current();
    const h = s?.yields.find((x) => x.id === yieldId);
    if (!h) return undefined;
    const next: YieldHold = { ...h, state: 'started-now', releasedAt: Date.now(), by: MOCK_ACTOR };
    putYield(next);
    const run = s?.runs.find((r) => r.yieldId === yieldId && r.state === 'running');
    if (run) {
      putRun({ ...run, startedAt: Date.now() });
      mockFinish(run.id);
    }
    return next;
  }
  const hold = await api<YieldHold>(`/api/project-protocol/yields/${encodeURIComponent(yieldId)}/start-now`, { method: 'POST', body: {} });
  putYield(hold);
  return hold;
}

/** "Dispatch a fix": a Claude Code session in the project with the diagnosis attached. */
export type DispatchMode = 'headless' | 'terminal';

export async function dispatchFix(problemKey: string, note: string, mode: DispatchMode = 'headless'): Promise<FixDispatch> {
  const body = { problemKey, ...(note.trim() ? { note: note.trim() } : {}), ...(mode === 'terminal' ? { mode } : {}) };
  if (isMock()) {
    const s = current();
    const p = s?.problems.find((x) => x.key === problemKey);
    if (!s || !p || !p.projectId || !p.machineId) throw { what: 'The fix session did not start.', why: 'This problem has no project folder to start in.', next: 'Open the problem from its project.' };
    const d: FixDispatch = { id: newId('fix'), problemKey, projectId: p.projectId, machineId: p.machineId, sessionId: null, state: 'starting', by: MOCK_ACTOR, at: Date.now() };
    putDispatch(d);
    // The demo has no new session to open, so it links the project's newest session on that machine.
    const sessions = Object.values(useOrgus.getState().sessions).filter((x) => x.projectId === p.projectId && x.machineId === p.machineId);
    const newest = sessions.sort((a, b) => b.lastActivity - a.lastActivity)[0];
    setTimeout(() => putDispatch({ ...d, state: newest ? 'started' : 'failed', sessionId: newest?.id ?? null, ...(newest ? {} : { reason: 'No Claude Code on that machine.' }) }), 1200);
    return d;
  }
  const d = await api<FixDispatch>('/api/project-protocol/dispatch', { method: 'POST', body });
  putDispatch(d);
  return d;
}

export async function saveCheckSettings(settings: NativeCheckSettings): Promise<NativeCheckSettings> {
  if (isMock()) {
    update((s) => ({ ...s, settings }));
    return settings;
  }
  const saved = await api<NativeCheckSettings>('/api/project-protocol/settings', { method: 'PUT', body: settings });
  update((s) => ({ ...s, settings: saved }));
  return saved;
}

